import { useState, useEffect, useCallback } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import clsx from 'clsx';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastOptions {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
}

const typeConfig: Record<ToastType, { icon: React.ReactNode; className: string; iconBg: string }> = {
  success: {
    icon: <CheckCircle className="h-4 w-4" />,
    className: 'border-hhp-success/25',
    iconBg: 'bg-hhp-success-bg text-hhp-success',
  },
  error: {
    icon: <XCircle className="h-4 w-4" />,
    className: 'border-hhp-danger/25',
    iconBg: 'bg-hhp-danger-bg text-hhp-danger',
  },
  warning: {
    icon: <AlertTriangle className="h-4 w-4" />,
    className: 'border-hhp-warning/25',
    iconBg: 'bg-hhp-warning-bg text-hhp-warning',
  },
  info: {
    icon: <Info className="h-4 w-4" />,
    className: 'border-hhp-navy-text/20',
    iconBg: 'bg-hhp-navy-text/8 text-hhp-navy-text',
  },
};

let toastId = 0;

let listeners: ((toasts: ToastOptions[]) => void)[] = [];
let toasts: ToastOptions[] = [];

function notify() {
  listeners.forEach((fn) => fn([...toasts]));
}

export function useToast() {
  const [state, setState] = useState<ToastOptions[]>(toasts);

  useEffect(() => {
    listeners.push(setState);
    return () => {
      listeners = listeners.filter((l) => l !== setState);
    };
  }, []);

  const add = useCallback(
    (options: Omit<ToastOptions, 'id'>) => {
      const id = `toast-${++toastId}`;
      const newToast: ToastOptions = { ...options, id, duration: options.duration ?? 4000 };
      toasts = [...toasts, newToast];
      notify();

      if (newToast.duration && newToast.duration > 0) {
        setTimeout(() => {
          toasts = toasts.filter((t) => t.id !== id);
          notify();
        }, newToast.duration);
      }

      return id;
    },
    []
  );

  const remove = useCallback((id: string) => {
    toasts = toasts.filter((t) => t.id !== id);
    notify();
  }, []);

  return { toasts: state, add, remove };
}

export function ToastContainer({
  toasts,
  remove,
}: {
  toasts: ToastOptions[];
  remove: (id: string) => void;
}) {
  return (
    <div
      className="fixed bottom-20 right-4 z-[90] flex flex-col gap-2 lg:bottom-4"
      aria-live="polite"
    >
      {toasts.map((toast) => {
        const config = typeConfig[toast.type];
        return (
          <div
            key={toast.id}
            className={clsx(
              'flex w-80 items-start gap-3 rounded-2xl border bg-hhp-white p-4 shadow-lg animate-slide-up',
              config.className,
            )}
          >
            <span className={clsx('mt-0.5 shrink-0 rounded-lg p-1', config.iconBg)}>
              {config.icon}
            </span>
            <div className="flex-1">
              <p className="text-sm font-semibold text-hhp-navy-text">{toast.title}</p>
              {toast.message && (
                <p className="mt-0.5 text-xs text-hhp-muted">{toast.message}</p>
              )}
            </div>
            <button
              onClick={() => remove(toast.id)}
              className="shrink-0 rounded-lg p-1 text-hhp-faint hover:text-hhp-navy-text hover:bg-hhp-cream transition-colors"
              aria-label="Dismiss"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
